/**
 * MainMenu - Classic Ashford-style Main Menu Controller with Real Social Login (Discord & Google OAuth2)
 * Features:
 * - Real Discord OAuth2 & Google Identity Services (GIS)
 * - Authentication Gate: '캐릭터 생성', '이어하기' require login
 * - Character Select Dialog on '이어하기' (Continue):
 *   - If characters exist -> Select character modal with stats, level, role preview & enter world
 *   - If NO characters exist -> Shows '캐릭터 없음' warning & guides user to create a new character
 * - Persistent User Session via localStorage & AccountSaveSystem
 */

import { Settings, BIND_ACTIONS, displayKey, Language } from '../../core/Settings';
import { AccountSaveSystem, SavedCharacterSlot } from '../../core/AccountSaveSystem';
import { ApiClient } from '../../core/ApiClient';
import { Currency } from '../../core/Currency';

export interface UserSession {
  provider: 'discord' | 'google';
  name: string;
  email: string;
  avatar: string;
  avatarUrl?: string;
  userId?: string;
  gold: number;
  gems: number;
}

const STORAGE_KEY = 'terra_anima_user_session';

// Production OAuth2 Client IDs provided by user
const DISCORD_CLIENT_ID = '1554404779755839490';
const GOOGLE_CLIENT_ID = '986860957690-8n6a9cdi7hbfp5jctpf5j1oo0jqmt5uh.apps.googleusercontent.com';

export class MainMenu {
  private el: HTMLElement | null = null;
  private onNewGameCb: (() => void) | null = null;
  private onContinueCb: ((charSlot: SavedCharacterSlot) => void) | null = null;
  private onShopCb: (() => void) | null = null;
  private currentUser: UserSession | null = null;

  public init(callbacks: {
    onNewGame: () => void;
    onContinue: (charSlot: SavedCharacterSlot) => void;
    onShop: () => void;
  }): void {
    this.onNewGameCb = callbacks.onNewGame;
    this.onContinueCb = callbacks.onContinue;
    this.onShopCb = callbacks.onShop;

    // 1. Check if returning from Discord OAuth2 Redirect (has #access_token=...)
    this.handleOAuthCallback();

    // 2. Load persisted session
    this.loadSession();
    if (this.currentUser) {
      AccountSaveSystem.ensureAccountExists(this.currentUser);
      const accKey = AccountSaveSystem.getAccountKey(this.currentUser);
      AccountSaveSystem.syncCharactersFromServer(accKey).then(() => {
        this.updateAuthUI();
      });
    }

    const uiRoot = document.getElementById('ui');
    if (!uiRoot) return;

    this.render(uiRoot);
    this.bindEvents();
    this.updateAuthUI();
    this.initGoogleOAuth();
  }

  public show(): void {
    if (this.el) {
      this.el.classList.remove('hidden');
      this.updateAuthUI();
    }
  }

  public hide(): void {
    if (this.el) {
      this.el.classList.add('hidden');
    }
  }

  public getCurrentUser(): UserSession | null {
    return this.currentUser;
  }

  private settingsOverlayCb: (() => void) | null = null;

  /** 인게임(ESC 메뉴)에서 설정 창만 띄운다. 닫으면 메인메뉴를 다시 숨기고 onClose 호출. */
  public openSettingsOverlay(onClose: () => void): void {
    if (!this.el) return;
    this.settingsOverlayCb = onClose;
    this.el.classList.remove('hidden');
    this.el.querySelector('#settings-modal')?.classList.remove('hidden');
  }

  private finishSettingsOverlay(): void {
    if (!this.settingsOverlayCb) return;
    const cb = this.settingsOverlayCb;
    this.settingsOverlayCb = null;
    this.hide();
    cb();
  }

  private handleOAuthCallback(): void {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      if (accessToken) {
        window.history.replaceState(null, '', '/');
        this.fetchDiscordProfile(accessToken);
      }
    }
  }

  private async fetchDiscordProfile(accessToken: string): Promise<void> {
    try {
      let displayName = '디스코드 모험가';
      let email = 'user@discord';
      let avatarUrl = '';
      let userId: string | undefined;

      try {
        const loginRes = await ApiClient.loginWithProvider('discord', accessToken);
        displayName = loginRes.account.name || displayName;
        email = loginRes.account.email || email;
        avatarUrl = loginRes.account.avatarUrl || avatarUrl;
        userId = String(loginRes.account.id);
        console.log('✨ [Server Auth] Logged in successfully:', loginRes.account);
      } catch (srvErr) {
        console.warn('⚠️ [Server Auth] Backend login failed, falling back to direct Discord fetch', srvErr);
        const res = await fetch('https://discord.com/api/users/@me', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!res.ok) {
          throw new Error(`Discord API returned ${res.status}`);
        }
        const data = await res.json();
        displayName = data.global_name || data.username || displayName;
        email = data.email || `${data.username}@discord`;
        if (data.avatar) {
          avatarUrl = `https://cdn.discordapp.com/avatars/${data.id}/${data.avatar}.png?size=128`;
        }
        userId = data.id;
      }

      const session: UserSession = {
        provider: 'discord',
        name: displayName,
        email,
        avatar: '🎮',
        avatarUrl,
        userId,
        gold: Currency.toCopper({ gold: 100 }),
        gems: 300,
      };

      this.saveSession(session);
      const accKey = AccountSaveSystem.getAccountKey(session);
      await AccountSaveSystem.syncCharactersFromServer(accKey);
      this.updateAuthUI();
    } catch (err) {
      console.error('Failed to fetch Discord user profile', err);
      alert('디스코드 프로필 정보를 가져오는 중 오류가 발생했습니다.');
    }
  }

  private loadSession(): void {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        this.currentUser = JSON.parse(data);
      }
    } catch (e) {
      console.warn('Failed to load user session', e);
      this.currentUser = null;
    }
  }

  private saveSession(session: UserSession | null): void {
    this.currentUser = session;
    if (session) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      // 자동 로그인 및 연동 시 관리자 페이지 accountsData에도 계정 등록(캐릭터는 0개)
      AccountSaveSystem.ensureAccountExists(session);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    this.updateAuthUI();
  }

  private initGoogleOAuth(): void {
    const checkGis = () => {
      const googleObj = (window as any).google;
      if (googleObj && googleObj.accounts && googleObj.accounts.id) {
        googleObj.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response: any) => this.handleGoogleCredentialResponse(response),
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        console.log('✅ [Google Identity Services] Initialized successfully with Client ID');
      } else {
        setTimeout(checkGis, 300);
      }
    };
    checkGis();
  }

  private handleGoogleCredentialResponse(response: any): void {
    try {
      const idToken = response.credential;
      if (!idToken) return;

      const base64Url = idToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const profile = JSON.parse(jsonPayload);
      console.log('✨ [Google OAuth2] Logged in user profile:', profile);

      const session: UserSession = {
        provider: 'google',
        name: profile.name || profile.given_name || '구글 모험가',
        email: profile.email || 'explorer@gmail.com',
        avatar: '🌐',
        avatarUrl: profile.picture || '',
        userId: profile.sub,
        gold: Currency.toCopper({ gold: 100 }),
        gems: 300,
      };

      this.saveSession(session);

      const loginModal = this.el?.querySelector('#login-required-modal') as HTMLElement;
      loginModal?.classList.add('hidden');
    } catch (err) {
      console.error('Failed to parse Google credentials', err);
      alert('구글 로그인 토큰 처리 중 오류가 발생했습니다.');
    }
  }

  private startDiscordLogin(): void {
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/discord/callback`);
    const scope = encodeURIComponent('identify email');
    const discordAuthUrl = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}`;
    window.location.href = discordAuthUrl;
  }

  private startGoogleLogin(): void {
    const googleObj = (window as any).google;
    if (googleObj && googleObj.accounts && googleObj.accounts.id) {
      googleObj.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          this.startGoogleTokenClient();
        }
      });
    } else {
      this.startGoogleTokenClient();
    }
  }

  private startGoogleTokenClient(): void {
    const googleObj = (window as any).google;
    if (googleObj && googleObj.accounts && googleObj.accounts.oauth2) {
      const client = googleObj.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
        callback: async (tokenResponse: any) => {
          if (tokenResponse && tokenResponse.access_token) {
            try {
              let displayName = '구글 모험가';
              let email = 'explorer@gmail.com';
              let avatarUrl = '';
              let userId: string | undefined;

              try {
                const loginRes = await ApiClient.loginWithProvider('google', tokenResponse.access_token);
                displayName = loginRes.account.name || displayName;
                email = loginRes.account.email || email;
                avatarUrl = loginRes.account.avatarUrl || avatarUrl;
                userId = String(loginRes.account.id);
                console.log('✨ [Server Auth] Google logged in successfully:', loginRes.account);
              } catch (srvErr) {
                console.warn('⚠️ [Server Auth] Backend Google login failed, falling back to direct userinfo', srvErr);
                const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                  headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
                });
                const userInfo = await res.json();
                displayName = userInfo.name || displayName;
                email = userInfo.email || email;
                avatarUrl = userInfo.picture || avatarUrl;
                userId = userInfo.sub;
              }

              const session: UserSession = {
                provider: 'google',
                name: displayName,
                email,
                avatar: '🌐',
                avatarUrl,
                userId,
                gold: Currency.toCopper({ gold: 100 }),
                gems: 300,
              };

              this.saveSession(session);
              const accKey = AccountSaveSystem.getAccountKey(session);
              await AccountSaveSystem.syncCharactersFromServer(accKey);
              this.updateAuthUI();

              const loginModal = this.el?.querySelector('#login-required-modal') as HTMLElement;
              loginModal?.classList.add('hidden');
            } catch (err) {
              console.error('Failed to process Google login', err);
            }
          }
        },
      });
      client.requestAccessToken();
    } else {
      alert('Google 로그인 라이브러리가 아직 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.');
    }
  }

  private render(container: HTMLElement): void {
    const wrapper = document.createElement('div');
    wrapper.id = 'main-menu-screen';
    wrapper.className = 'screen menu-bg';

    wrapper.innerHTML = `
      <!-- LEFT TITLE & MENU COLUMN -->
      <div class="title-left">
        <div class="title-brand" style="display: flex; align-items: center; gap: 14px;">
          <img src="/assets/icon-192.png" alt="Terra Anima Solaria Crest" style="width: 58px; height: 58px; border-radius: 10px; border: 1.5px solid rgba(246, 216, 112, 0.6); box-shadow: 0 4px 16px rgba(0,0,0,0.6); flex-shrink: 0;" />
          <div>
            <h1 class="title-logo" style="font-size: clamp(22px, 2.2vw, 32px); line-height: 1.15;">TERRA ANIMA ONLINE</h1>
            <p class="title-sub" style="color: #fde047; font-weight: 600; font-size: 13px; margin-top: 2px;">Solaria Chronicle (솔라리아 연대기)</p>
          </div>
        </div>

        <!-- AUTHENTICATION CARD / LOGIN BUTTONS -->
        <div class="menu-auth-card" id="menu-auth-card">
          <div class="menu-auth-header">
            <span>소셜 계정 접속 상태</span>
            <span id="auth-status-badge" class="menu-auth-badge">🔒 미로그인</span>
          </div>

          <!-- Logged Out View: Discord & Google Login Buttons -->
          <div id="auth-logged-out" class="auth-btn-row">
            <button id="btn-login-discord" class="auth-login-btn discord" title="디스코드 공식 계정 로그인">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.893.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
              <span>디스코드 로그인</span>
            </button>
            <button id="btn-login-google" class="auth-login-btn google" title="구글 계정 로그인">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/></svg>
              <span>구글 로그인</span>
            </button>
          </div>

          <!-- Logged In View: User Profile Chip & Logout -->
          <div id="auth-logged-in" class="auth-user-profile hidden">
            <div class="auth-user-info">
              <span class="auth-user-avatar" id="auth-avatar-icon">
                <img id="auth-avatar-img" src="" alt="avatar" style="width:100%; height:100%; border-radius:50%; object-fit:cover; display:none;" />
                <span id="auth-avatar-emoji">🛡️</span>
              </span>
              <div class="auth-user-meta">
                <span class="auth-user-name" id="auth-display-name">모험가</span>
                <span class="auth-user-provider" id="auth-provider-name">소셜 계정 인증됨</span>
              </div>
            </div>
            <button id="btn-auth-logout" class="auth-logout-btn">로그아웃</button>
          </div>
        </div>

        <div class="title-menu">
          <button id="btn-menu-continue" class="menu-btn primary locked">
            <span class="btn-icon">⚔️</span>
            <span class="btn-text">월드 접속하기</span>
            <small id="continue-subtext">캐릭터 선택 및 월드 접속</small>
          </button>

          <button id="btn-menu-settings" class="menu-btn">
            <span class="btn-icon">⚙️</span>
            <span class="btn-text">설정</span>
            <small>그래픽 · 사운드 · 조작키 · 언어 설정</small>
          </button>

          <button id="btn-menu-shop" class="menu-btn">
            <span class="btn-icon">💎</span>
            <span class="btn-text">상점</span>
            <small>보석 · 편의 아이템 및 패키지</small>
          </button>

          <button id="btn-menu-editor" class="menu-btn" style="border-left-color: #38bdf8;">
            <span class="btn-icon">🏔️</span>
            <span class="btn-text">3D 월드/하우징 에디터</span>
            <small>지형 높낮이 브러시 · 건물 배치 · 주거 필지 생성</small>
          </button>

          <button id="btn-menu-quit" class="menu-btn danger">
            <span class="btn-icon">🚪</span>
            <span class="btn-text">종료</span>
          </button>
        </div>

        <p id="menu-error" class="error-text"></p>
      </div>

      <!-- RIGHT ACCOUNT CHIP -->
      <div class="title-account">
        <div class="acc-chip">
          <span class="acc-avatar" id="chip-acc-avatar">
            <img id="chip-acc-avatar-img" src="" alt="avatar" style="width:20px; height:20px; border-radius:50%; object-fit:cover; display:none;" />
            <span id="chip-acc-avatar-emoji">👤</span>
          </span>
          <span class="acc-name" id="menu-acc-name">방문자 모험가</span>
          <span class="acc-gold" id="menu-acc-gold">🪙 100G</span>
          <span class="acc-gems" id="menu-acc-gems">💎 100</span>
        </div>
      </div>

      <!-- BOTTOM NOTICES & LEGAL -->
      <div class="title-footer-legal">
        <button id="btn-open-notice" class="link-btn">📢 공지사항</button>
        <span class="sep">·</span>
        <a href="#privacy" class="link-btn">개인정보처리방침</a>
        <span class="sep">·</span>
        <a href="#terms" class="link-btn">이용약관</a>
      </div>

      <!-- CHARACTER SELECT MODAL DIALOG (월드 접속하기 전용) -->
      <div id="char-select-modal" class="modal-backdrop hidden">
        <div class="modal-card" style="max-width: 580px;">
          <div class="modal-header">
            <h2>⚔️ 캐릭터 선택</h2>
            <button id="btn-close-char-select" class="btn-close">✕</button>
          </div>
          <div class="char-select-body" style="padding: 16px 20px;">
            <p style="font-size: 13px; color: #94a3b8; margin-bottom: 12px;">접속할 모험가 캐릭터를 선택해 주세요.</p>
            <div id="char-select-list" style="display: flex; flex-direction: column; gap: 10px; max-height: 50vh; overflow-y: auto;">
              <!-- Dynamically populated character slots -->
            </div>
          </div>
          <div class="modal-footer" style="display: flex; justify-content: space-between; align-items: center;">
            <button id="btn-char-select-new" class="btn-small" style="background:#1e293b; border:1px solid #475569; padding:8px 14px; color:#cbd5e1; cursor:pointer;">
              + 캐릭터 생성
            </button>
            <button id="btn-char-select-enter" class="btn-primary" style="padding:8px 20px;" disabled>
              월드 접속하기
            </button>
          </div>
        </div>
      </div>

      <!-- NO CHARACTER WARNING MODAL DIALOG -->
      <div id="no-char-modal" class="modal-backdrop hidden">
        <div class="modal-card" style="max-width: 440px; text-align: center;">
          <div class="modal-header">
            <h2>⚠️ 캐릭터 없음</h2>
            <button id="btn-close-no-char" class="btn-close">✕</button>
          </div>
          <div style="padding: 24px 20px;">
            <div style="font-size: 40px; margin-bottom: 12px;">👤❌</div>
            <p style="font-size: 15px; color: #f8fafc; font-weight: 700; margin-bottom: 8px;">
              저장된 캐릭터가 없습니다!
            </p>
            <p style="font-size: 13px; color: #94a3b8; line-height: 1.5; margin-bottom: 20px;">
              현재 계정에 생성된 캐릭터가 없습니다.<br>
              먼저 <b>'캐릭터 생성'</b>을 진행하여 성인식과 8질문 시험을 거쳐 테라 아니마 세계로 입장해 주세요.
            </p>
            <button id="btn-goto-create-char" class="btn-primary" style="width: 100%; padding: 10px; font-size: 14px;">
              ✨ 캐릭터 생성하기
            </button>
          </div>
        </div>
      </div>

      <!-- LOGIN REQUIRED MODAL DIALOG -->
      <div id="login-required-modal" class="modal-backdrop hidden">
        <div class="modal-card" style="max-width: 440px;">
          <div class="modal-header">
            <h2>🔒 로그인이 필요합니다</h2>
            <button id="btn-close-login-modal" class="btn-close">✕</button>
          </div>
          <div class="notice-body" style="padding: 20px; text-align: center;">
            <p style="font-size: 15px; color: #f8fafc; margin-bottom: 12px; line-height: 1.5;">
              <b>테라 아니마</b>에 오신 것을 환영합니다.<br>
              캐릭터 생성 및 게임 진행을 위해<br>
              <span style="color: #fef08a; font-weight: 700;">디스코드</span> 또는 <span style="color: #f87171; font-weight: 700;">구글</span> 공식 계정으로 로그인해 주세요.
            </p>
            <div style="display: flex; flex-direction: column; gap: 10px; margin-top: 16px;">
              <button id="btn-modal-discord" class="auth-login-btn discord" style="width: 100%;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.893.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                <span>디스코드 공식 계정 로그인</span>
              </button>
              <button id="btn-modal-google" class="auth-login-btn google" style="width: 100%;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/></svg>
                <span>구글 계정 로그인</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- SETTINGS MODAL DIALOG -->
      <div id="settings-modal" class="modal-backdrop hidden">
        <div class="modal-card">
          <div class="modal-header">
            <h2>⚙️ 환경 설정 (Settings)</h2>
            <button id="btn-close-settings" class="btn-close">✕</button>
          </div>

          <div class="settings-tabs">
            <button class="settings-tab-btn active" data-tab="graphics">🖥️ 그래픽</button>
            <button class="settings-tab-btn" data-tab="sound">🔊 사운드</button>
            <button class="settings-tab-btn" data-tab="controls">🎮 조작키</button>
            <button class="settings-tab-btn" data-tab="others">🌐 기타 (언어)</button>
          </div>

          <div class="settings-content">
            <!-- TAB 1: GRAPHICS -->
            <div id="tab-graphics" class="settings-pane active">
              <div class="form-row">
                <label>화면 배율 / 줌 레벨</label>
                <select id="opt-zoom-level">
                  <option value="0.75">75% (넓은 시야)</option>
                  <option value="1.0" selected>100% (표준 픽셀 규격)</option>
                  <option value="1.25">125% (확대)</option>
                  <option value="1.5">150% (정밀 클로즈업)</option>
                </select>
              </div>
              <div class="form-row">
                <label>부드러운 카메라 추종</label>
                <input type="checkbox" id="opt-cam-smooth" checked>
              </div>
              <div class="form-row">
                <label>데미지 플로팅 텍스트 표시</label>
                <input type="checkbox" id="opt-dmg-text" checked>
              </div>
            </div>

            <!-- TAB 2: SOUND -->
            <div id="tab-sound" class="settings-pane">
              <div class="form-row">
                <label>마스터 볼륨</label>
                <input type="range" id="opt-vol-master" min="0" max="100" value="80">
              </div>
              <div class="form-row">
                <label>배경 음악 (BGM / 앰비언트)</label>
                <input type="range" id="opt-vol-bgm" min="0" max="100" value="70">
              </div>
              <div class="form-row">
                <label>전투 및 효과음 (SFX)</label>
                <input type="range" id="opt-vol-sfx" min="0" max="100" value="85">
              </div>
            </div>

            <!-- TAB 3: CONTROLS -->
            <div id="tab-controls" class="settings-pane">
              <div class="bind-list">
                <div class="bind-row"><span>이동 조작</span><kbd>W / A / S / D</kbd></div>
                <div class="bind-row"><span>적 순환 타겟팅</span><kbd>Tab</kbd></div>
                <div class="bind-row"><span>타겟 해제</span><kbd>ESC</kbd></div>
                <div id="bind-rows"></div>
              </div>
              <div id="bind-warning-msg" style="display:none; margin-top:10px; padding:8px 12px; background:rgba(239,68,68,0.15); border:1px solid #ef4444; border-radius:6px; color:#fca5a5; font-size:12px; text-align:center;">
                ⚠️ 시스템 예약 키이므로 단축키로 지정할 수 없습니다.
              </div>
            </div>

            <!-- TAB 4: OTHERS & LANGUAGE -->
            <div id="tab-others" class="settings-pane">
              <div class="form-row">
                <label>표시 언어 (Language)</label>
                <select id="opt-language">
                  <option value="ko" selected>한국어 (Korean)</option>
                  <option value="en">English</option>
                  <option value="ja">日本語 (Japanese)</option>
                </select>
              </div>
              <div style="font-size:11px; color:#94a3b8; margin-top:6px;">선택한 언어는 저장되며, 번역은 순차적으로 적용됩니다.</div>
            </div>
          </div>

          <div class="modal-footer">
            <button id="btn-save-settings" class="btn-primary">저장 및 닫기</button>
          </div>
        </div>
      </div>

      <!-- NOTICE MODAL -->
      <div id="notice-modal" class="modal-backdrop hidden">
        <div class="modal-card">
          <div class="modal-header">
            <h2>📢 솔라리스 왕국 공지사항</h2>
            <button id="btn-close-notice" class="btn-close">✕</button>
          </div>
          <div class="notice-body">
            <h3>⚔️ 데라 아니마 온라인 - 솔라리아 연대기 (Terra Anima Online) 공식 개시</h3>
            <p>• <b>세계관</b>: 네 개의 태양(솔라리스, 아우로라, 솔, 헬리오스)과 두 개의 달이 지배하는 차원 세계의 서막이 열렸습니다.</p>
            <p>• <b>특징</b>: DAoC 전통 탭 타겟팅 전투, 6대 직업군, 12세 성인식 심리 테스트 성향 시스템 및 자율 생태계를 경험하세요.</p>
          </div>
          <div class="modal-footer">
            <button id="btn-confirm-notice" class="btn-primary">확인</button>
          </div>
        </div>
      </div>
    `;

    container.appendChild(wrapper);
    this.el = wrapper;
  }

  private updateAuthUI(): void {
    if (!this.el) return;

    const loggedOutSection = this.el.querySelector('#auth-logged-out') as HTMLElement;
    const loggedInSection = this.el.querySelector('#auth-logged-in') as HTMLElement;
    const statusBadge = this.el.querySelector('#auth-status-badge') as HTMLElement;

    const btnContinue = this.el.querySelector('#btn-menu-continue') as HTMLElement;
    const continueSubtext = this.el.querySelector('#continue-subtext') as HTMLElement;

    // Right Chip
    const chipName = this.el.querySelector('#menu-acc-name') as HTMLElement;
    const chipAvatarImg = this.el.querySelector('#chip-acc-avatar-img') as HTMLImageElement;
    const chipAvatarEmoji = this.el.querySelector('#chip-acc-avatar-emoji') as HTMLElement;
    const chipGold = this.el.querySelector('#menu-acc-gold') as HTMLElement;
    const chipGems = this.el.querySelector('#menu-acc-gems') as HTMLElement;

    if (this.currentUser) {
      // Authenticated state
      loggedOutSection?.classList.add('hidden');
      loggedInSection?.classList.remove('hidden');

      if (statusBadge) {
        statusBadge.textContent = '✅ 인증 완료';
        statusBadge.className = 'menu-auth-badge authenticated';
      }

      const displayName = this.el.querySelector('#auth-display-name') as HTMLElement;
      const providerName = this.el.querySelector('#auth-provider-name') as HTMLElement;
      const avatarImg = this.el.querySelector('#auth-avatar-img') as HTMLImageElement;
      const avatarEmoji = this.el.querySelector('#auth-avatar-emoji') as HTMLElement;

      if (displayName) displayName.textContent = this.currentUser.name;
      if (providerName) {
        providerName.textContent = this.currentUser.provider === 'discord'
          ? `디스코드 연동 (${this.currentUser.email})`
          : `구글 연동 (${this.currentUser.email})`;
      }

      // Avatar image vs emoji
      if (this.currentUser.avatarUrl && avatarImg && chipAvatarImg) {
        avatarImg.src = this.currentUser.avatarUrl;
        avatarImg.style.display = 'block';
        if (avatarEmoji) avatarEmoji.style.display = 'none';

        chipAvatarImg.src = this.currentUser.avatarUrl;
        chipAvatarImg.style.display = 'inline-block';
        if (chipAvatarEmoji) chipAvatarEmoji.style.display = 'none';
      } else {
        if (avatarImg) avatarImg.style.display = 'none';
        if (avatarEmoji) {
          avatarEmoji.textContent = this.currentUser.avatar;
          avatarEmoji.style.display = 'inline-block';
        }
        if (chipAvatarImg) chipAvatarImg.style.display = 'none';
        if (chipAvatarEmoji) {
          chipAvatarEmoji.textContent = this.currentUser.avatar;
          chipAvatarEmoji.style.display = 'inline-block';
        }
      }

      // Check if user has characters saved
      const accKey = AccountSaveSystem.getAccountKey(this.currentUser);
      const chars = AccountSaveSystem.getCharactersForAccount(accKey);

      btnContinue?.classList.remove('locked');
      if (chars.length > 0) {
        if (continueSubtext) continueSubtext.textContent = `저장된 캐릭터 (${chars.length}명) 선택 및 접속`;
      } else {
        if (continueSubtext) continueSubtext.textContent = `캐릭터 선택 및 새 캐릭터 생성`;
      }

      // Update right chip
      if (chipName) chipName.textContent = this.currentUser.name;
      if (chipGold) chipGold.textContent = `🪙 ${Currency.format(this.currentUser.gold)}`;
      if (chipGems) chipGems.textContent = `💎 ${this.currentUser.gems.toLocaleString()}`;
    } else {
      // Unauthenticated state
      loggedOutSection?.classList.remove('hidden');
      loggedInSection?.classList.add('hidden');

      if (statusBadge) {
        statusBadge.textContent = '🔒 로그인 필요';
        statusBadge.className = 'menu-auth-badge';
      }

      btnContinue?.classList.add('locked');
      if (continueSubtext) continueSubtext.textContent = '캐릭터 선택 및 월드 접속';

      if (chipName) chipName.textContent = '방문자 (미로그인)';
      if (chipAvatarImg) chipAvatarImg.style.display = 'none';
      if (chipAvatarEmoji) {
        chipAvatarEmoji.textContent = '👤';
        chipAvatarEmoji.style.display = 'inline-block';
      }
      if (chipGold) chipGold.textContent = `🪙 ${Currency.format(Currency.toCopper({ gold: 100 }))}`;
      if (chipGems) chipGems.textContent = '💎 100';
    }
  }

  private selectedSlot: SavedCharacterSlot | null = null;

  public async openCharacterSelectModal(preselectId?: string): Promise<void> {
    const accKey = AccountSaveSystem.getAccountKey(this.currentUser);
    let chars = AccountSaveSystem.getCharactersForAccount(accKey);

    // 서버 DB와 실시간 동기화
    if (this.currentUser) {
      try {
        chars = await AccountSaveSystem.syncCharactersFromServer(accKey);
      } catch (e) {
        console.warn('[MainMenu] Server character sync error:', e);
      }
    }

    if (chars.length === 0) {
      // Show 'No Character' Modal
      const noCharModal = this.el?.querySelector('#no-char-modal') as HTMLElement;
      noCharModal?.classList.remove('hidden');
      return;
    }

    const selectModal = this.el?.querySelector('#char-select-modal') as HTMLElement;
    const listContainer = this.el?.querySelector('#char-select-list') as HTMLElement;
    const enterBtn = this.el?.querySelector('#btn-char-select-enter') as HTMLButtonElement;

    if (!listContainer || !selectModal) return;

    const wantId = preselectId ?? this.selectedSlot?.id;
    this.selectedSlot = chars.find((c) => c.id === wantId) || chars[0]; // 선택 유지, 없으면 첫 캐릭터
    listContainer.innerHTML = '';

    chars.forEach((slot, idx) => {
      const card = document.createElement('div');
      card.className = `char-select-card ${this.selectedSlot?.id === slot.id ? 'active' : ''}`;
      card.style.cssText = `
        display: flex; align-items: center; justify-content: space-between;
        background: ${this.selectedSlot?.id === slot.id ? 'linear-gradient(135deg, rgba(30,58,138,0.9), rgba(15,23,42,0.95))' : 'rgba(15,23,42,0.85)'};
        border: 1.5px solid ${this.selectedSlot?.id === slot.id ? '#f59e0b' : '#334155'};
        border-radius: 8px; padding: 12px 16px; cursor: pointer; transition: all 0.15s;
      `;

      const isAdminSlot = slot.class === 'admin' || (slot.class && slot.class.includes('관리자')) || (slot as any).isAdmin;
      const classIcon = isAdminSlot ? '👑' : slot.class === 'paladin' ? '🛡️' : slot.class === 'wizard' ? '🔮' : slot.class === 'bard' ? '🎵' : '⚔️';
      const classDisplayName = isAdminSlot ? '👑 총괄 관리자 (Lord British)' : slot.class;

      card.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px; flex: 1;">
          <span style="font-size: 28px;">${classIcon}</span>
          <div>
            <div style="font-size: 15px; font-weight: 800; color: #fef08a;">
              ${slot.name} <span style="font-size: 12px; color: #38bdf8; font-weight: 700; margin-left: 6px;">Lv.${slot.level}</span>
            </div>
            <div style="font-size: 11.5px; color: #94a3b8; margin-top: 2px;">
              종족: ${slot.race || '솔라리안'} · 직업: ${classDisplayName} · 소지금: 🪙 ${Currency.format(slot.gold || 0)}
            </div>
          </div>
        </div>
        <div style="display: flex; align-items: center; gap: 10px;">
          <div style="text-align: right; font-size: 11px; color: #64748b;">
            ${this.selectedSlot?.id === slot.id ? '<span style="color: #4ade80; font-weight: 800;">선택됨 ✔</span>' : '클릭하여 선택'}
          </div>
          <button class="btn-delete-char" title="캐릭터 삭제" style="
            background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4);
            color: #fca5a5; font-size: 11px; padding: 4px 8px; border-radius: 6px; cursor: pointer;
            transition: all 0.15s;
          ">🗑️</button>
        </div>
      `;

      const delBtn = card.querySelector('.btn-delete-char') as HTMLButtonElement;
      if (delBtn) {
        delBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          if (!confirm(`[${slot.name}] 캐릭터를 정말로 삭제하시겠습니까?\n삭제된 캐릭터는 서버 DB와 로컬에서 영구 삭제됩니다.`)) return;
          await AccountSaveSystem.deleteCharacter(accKey, slot.id);
          if (this.selectedSlot?.id === slot.id) {
            this.selectedSlot = null;
          }
          this.openCharacterSelectModal();
        });
      }

      card.addEventListener('click', () => {
        this.selectedSlot = slot;
        this.openCharacterSelectModal(); // Re-render highlight
      });

      listContainer.appendChild(card);
    });

    // 빈 슬롯(기본 3칸 + 다이아 확장분) & 잠긴 슬롯(다이아 확장 안내)
    const limit = AccountSaveSystem.getSlotLimit(accKey);
    const cost = AccountSaveSystem.SLOT_EXPAND_GEM_COST;
    for (let i = chars.length; i < Math.max(limit, AccountSaveSystem.BASE_SLOTS); i++) {
      const empty = document.createElement('div');
      empty.style.cssText = `display:flex; align-items:center; justify-content:space-between; background:rgba(15,23,42,0.5);
        border:1.5px dashed #475569; border-radius:8px; padding:12px 16px; cursor:pointer; color:#94a3b8; font-size:13px;`;
      empty.innerHTML = `<span>➕ 빈 슬롯 ${i + 1}</span><span style="color:#fbbf24;font-weight:700;">캐릭터 생성</span>`;
      empty.addEventListener('click', () => {
        selectModal.classList.add('hidden');
        if (this.onNewGameCb) this.onNewGameCb();
      });
      listContainer.appendChild(empty);
    }
    if (limit < AccountSaveSystem.MAX_SLOTS) {
      const lock = document.createElement('div');
      lock.style.cssText = `display:flex; align-items:center; justify-content:space-between; background:rgba(30,20,60,0.5);
        border:1.5px dashed #6366f1; border-radius:8px; padding:12px 16px; cursor:pointer; color:#a5b4fc; font-size:13px;`;
      lock.innerHTML = `<span>🔒 슬롯 ${limit + 1} (잠김)</span><span style="color:#38bdf8;font-weight:700;">💎 ${cost} 로 확장</span>`;
      lock.addEventListener('click', () => {
        if (!this.currentUser) return;
        if (this.currentUser.gems < cost) {
          alert(`다이아가 부족합니다. (필요 💎${cost} / 보유 💎${this.currentUser.gems})`);
          return;
        }
        if (!confirm(`💎 ${cost}을(를) 사용해 캐릭터 슬롯을 1칸 확장하시겠습니까?`)) return;
        if (AccountSaveSystem.expandSlot(accKey)) {
          this.currentUser.gems -= cost;
          this.saveSession(this.currentUser);
          this.openCharacterSelectModal();
        }
      });
      listContainer.appendChild(lock);
    }

    if (enterBtn) {
      enterBtn.disabled = !this.selectedSlot;
    }

    selectModal.classList.remove('hidden');
  }

  private openLoginModal(): void {
    const loginModal = this.el?.querySelector('#login-required-modal') as HTMLElement;
    loginModal?.classList.remove('hidden');
  }

  private bindEvents(): void {
    if (!this.el) return;

    // 1. 월드 접속하기 (Continue/Enter World) - Must be logged in & open character select
    this.el.querySelector('#btn-menu-continue')?.addEventListener('click', () => {
      if (!this.currentUser) {
        this.openLoginModal();
        return;
      }
      this.openCharacterSelectModal();
    });

    // Character Select Modal Events
    const selectModal = this.el.querySelector('#char-select-modal') as HTMLElement;
    this.el.querySelector('#btn-close-char-select')?.addEventListener('click', () => {
      selectModal?.classList.add('hidden');
    });

    this.el.querySelector('#btn-char-select-new')?.addEventListener('click', () => {
      const nk = AccountSaveSystem.getAccountKey(this.currentUser);
      if (AccountSaveSystem.getCharactersForAccount(nk).length >= AccountSaveSystem.getSlotLimit(nk)) {
        alert('슬롯이 가득 찼습니다. 잠긴 슬롯을 💎 다이아로 확장해 주세요.');
        return;
      }
      selectModal?.classList.add('hidden');
      if (this.onNewGameCb) this.onNewGameCb();
    });

    this.el.querySelector('#btn-char-select-enter')?.addEventListener('click', () => {
      if (!this.selectedSlot) return;
      selectModal?.classList.add('hidden');
      this.hide();
      if (this.onContinueCb) this.onContinueCb(this.selectedSlot);
    });

    // No Character Modal Events
    const noCharModal = this.el.querySelector('#no-char-modal') as HTMLElement;
    this.el.querySelector('#btn-close-no-char')?.addEventListener('click', () => {
      noCharModal?.classList.add('hidden');
    });
    this.el.querySelector('#btn-goto-create-char')?.addEventListener('click', () => {
      noCharModal?.classList.add('hidden');
      if (this.onNewGameCb) this.onNewGameCb();
    });

    // 3. Login Buttons (Left Menu Card)
    this.el.querySelector('#btn-login-discord')?.addEventListener('click', () => {
      this.startDiscordLogin();
    });
    this.el.querySelector('#btn-login-google')?.addEventListener('click', () => {
      this.startGoogleLogin();
    });

    // 4. Logout Button
    this.el.querySelector('#btn-auth-logout')?.addEventListener('click', async () => {
      if (confirm('로그아웃 하시겠습니까?')) {
        await ApiClient.logout();
        this.saveSession(null);
      }
    });

    // 5. Login Modal Dialog Buttons
    const loginModal = this.el.querySelector('#login-required-modal') as HTMLElement;
    this.el.querySelector('#btn-close-login-modal')?.addEventListener('click', () => {
      loginModal?.classList.add('hidden');
    });
    this.el.querySelector('#btn-modal-discord')?.addEventListener('click', () => {
      this.startDiscordLogin();
    });
    this.el.querySelector('#btn-modal-google')?.addEventListener('click', () => {
      this.startGoogleLogin();
    });

    // 6. Settings button
    const settingsModal = this.el.querySelector('#settings-modal') as HTMLElement;
    this.el.querySelector('#btn-menu-settings')?.addEventListener('click', () => {
      settingsModal?.classList.remove('hidden');
    });
    this.el.querySelector('#btn-close-settings')?.addEventListener('click', () => {
      settingsModal?.classList.add('hidden');
      this.finishSettingsOverlay();
    });
    this.el.querySelector('#btn-save-settings')?.addEventListener('click', () => {
      persistSettings();
      settingsModal?.classList.add('hidden');
      this.finishSettingsOverlay();
    });

    // Settings tabs
    this.el.querySelectorAll('.settings-tab-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabKey = target.getAttribute('data-tab');
        if (!tabKey || !this.el) return;

        this.el.querySelectorAll('.settings-tab-btn').forEach((b) => b.classList.remove('active'));
        this.el.querySelectorAll('.settings-pane').forEach((p) => p.classList.remove('active'));

        target.classList.add('active');
        this.el.querySelector(`#tab-${tabKey}`)?.classList.add('active');
      });
    });

    // Settings: 조작키 변경 / 언어 / 옵션 저장
    const bindRowsEl = this.el.querySelector('#bind-rows') as HTMLElement | null;
    const bindWarnMsg = this.el.querySelector('#bind-warning-msg') as HTMLElement | null;
    const showBindWarn = (msg: string) => {
      if (!bindWarnMsg) return;
      bindWarnMsg.textContent = `⚠️ ${msg}`;
      bindWarnMsg.style.display = 'block';
    };
    let stopListen: (() => void) | null = null;
    const btnCss =
      'background:#1e293b; border:1px solid #475569; color:#f8fafc; padding:3px 10px; border-radius:4px; font-size:11px; cursor:pointer; min-width:64px;';
    const renderBindRows = () => {
      if (!bindRowsEl) return;
      bindRowsEl.innerHTML = '';
      for (const a of BIND_ACTIONS) {
        const row = document.createElement('div');
        row.className = 'bind-row';
        const label = document.createElement('span');
        label.textContent = a.label;
        const btn = document.createElement('button');
        btn.textContent = displayKey(Settings.getBinding(a.id));
        btn.style.cssText = btnCss;
        btn.addEventListener('click', () => {
          stopListen?.();
          if (bindWarnMsg) bindWarnMsg.style.display = 'none';
          btn.textContent = '키 입력 대기... (ESC 취소)';
          const onKeyDown = (e: KeyboardEvent) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            stopListen?.();
            if (e.key !== 'Escape') {
              const r = Settings.setBinding(a.id, e.key);
              if (!r.ok && r.message) showBindWarn(r.message);
            }
            renderBindRows();
          };
          stopListen = () => {
            window.removeEventListener('keydown', onKeyDown, true);
            stopListen = null;
          };
          window.addEventListener('keydown', onKeyDown, true);
        });
        row.append(label, btn);
        bindRowsEl.appendChild(row);
      }
      const reset = document.createElement('button');
      reset.textContent = '조작키 기본값으로 초기화';
      reset.style.cssText = btnCss + ' margin-top:10px; width:100%;';
      reset.addEventListener('click', () => {
        stopListen?.();
        Settings.resetBindings();
        if (bindWarnMsg) bindWarnMsg.style.display = 'none';
        renderBindRows();
      });
      bindRowsEl.appendChild(reset);
    };
    renderBindRows();

    const persistSettings = () => {
      const vals: Record<string, string | boolean> = {};
      this.el?.querySelectorAll<HTMLInputElement | HTMLSelectElement>('#settings-modal [id^="opt-"]').forEach((el) => {
        vals[el.id] = el instanceof HTMLInputElement && el.type === 'checkbox' ? el.checked : el.value;
      });
      Settings.setValues(vals);
      const langSel = this.el?.querySelector('#opt-language') as HTMLSelectElement | null;
      if (langSel) Settings.setLanguage(langSel.value as Language);
    };

    // 저장된 설정 값 복원
    const saved = Settings.getValues();
    for (const id of Object.keys(saved)) {
      const el = this.el.querySelector(`#${id}`) as HTMLInputElement | HTMLSelectElement | null;
      if (!el) continue;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') el.checked = !!saved[id];
      else el.value = String(saved[id]);
    }
    const langSel0 = this.el.querySelector('#opt-language') as HTMLSelectElement | null;
    if (langSel0) langSel0.value = Settings.getLanguage();

    // 7. Shop button
    this.el.querySelector('#btn-menu-shop')?.addEventListener('click', () => {
      if (this.onShopCb) this.onShopCb();
    });

    // 7-1. 3D World / Housing Editor button
    this.el.querySelector('#btn-menu-editor')?.addEventListener('click', () => {
      window.location.href = '/terrain_editor_3d.html';
    });

    // 8. Quit button (브라우저, APK/Capacitor/Cordova, Steam/Electron/Tauri 멀티 플랫폼 종료 지원)
    this.el.querySelector('#btn-menu-quit')?.addEventListener('click', () => {
      if (!confirm('게임을 종료하시겠습니까?')) return;

      const anyWindow = window as any;
      const anyNav = navigator as any;

      // 1) Electron / Steam 데스크톱 래퍼
      if (anyWindow.electronAPI?.quit) {
        anyWindow.electronAPI.quit();
        return;
      }
      if (anyWindow.ipcRenderer?.send) {
        anyWindow.ipcRenderer.send('app-quit');
        return;
      }

      // 2) Tauri 데스크톱 래퍼
      if (anyWindow.__TAURI__?.process?.exit) {
        anyWindow.__TAURI__.process.exit(0);
        return;
      }

      // 3) Android APK (Capacitor App 플러그인)
      if (anyWindow.Capacitor?.Plugins?.App?.exitApp) {
        anyWindow.Capacitor.Plugins.App.exitApp();
        return;
      }

      // 4) Android APK (Cordova / PhoneGap)
      if (anyNav.app?.exitApp) {
        anyNav.app.exitApp();
        return;
      }

      // 5) 표준 브라우저 창 닫기 시도
      try {
        window.close();
      } catch (e) {
        console.warn('window.close() failed', e);
      }

      // 6) 브라우저 팝업/스크립트로 열리지 않은 일반 탭이라 window.close()가 차단된 경우
      setTimeout(() => {
        if (!window.closed) {
          window.location.href = 'about:blank';
        }
      }, 100);
    });

    // 9. Notice Modal
    const noticeModal = this.el.querySelector('#notice-modal') as HTMLElement;
    this.el.querySelector('#btn-open-notice')?.addEventListener('click', () => {
      noticeModal?.classList.remove('hidden');
    });
    this.el.querySelector('#btn-close-notice')?.addEventListener('click', () => {
      noticeModal?.classList.add('hidden');
    });
    this.el.querySelector('#btn-confirm-notice')?.addEventListener('click', () => {
      noticeModal?.classList.add('hidden');
    });
  }
}
